import { Component, OnInit, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { Store } from '@ngxs/store';
import { MenuItem, MessageService } from 'primeng/api';
import { AvatarModule } from 'primeng/avatar';
import { ButtonModule } from 'primeng/button';
import { MenubarModule } from 'primeng/menubar';
import { MenuModule } from 'primeng/menu';
import { RippleModule } from 'primeng/ripple';
import { Logout } from '@core/states/auth.state';
import { ThemeService } from '@core/services/theme.service';

/**
 * @title MenuComponent
 * @since 1.0.0
 * 
 * @description The menu component provides the main navigation for the application.
 * It includes links to various sections, a logout option, and a theme toggle.
 * 
 */
@Component({
    selector: 'app-menu',
    imports: [
        RouterLink,
        MenuModule,
        RippleModule,
        MenubarModule,
        AvatarModule,
        ButtonModule
    ],
    templateUrl: './menu.component.html',
    styleUrls: ['./menu.component.scss']
})
export class MenuComponent implements OnInit {

	/**
     * The menu items displayed in the navigation menu.
     * @type {MenuItem[] | undefined}
     */
	items: MenuItem[] | undefined;

	/**
     * The username of the logged-in user.
     * @type {string | undefined}
     */
	username: string | undefined;

	/**
     * The short name (initials) of the logged-in user.
     * @type {string}
     */
	shortName: string = '';

	/**
     * Signal to track whether dark mode is enabled.
     * @type {Signal<boolean>}
     */
	isDarkMode = signal(false);

	/**
     * @param {Store} store - The NGXS store for managing application state.
     * @param {MessageService} messageService - Service for displaying messages to the user.
     * @param {ThemeService} themeService - Service for managing the application theme.
     * @since 1.0.0
     */
	constructor(
		private store: Store,
		private messageService: MessageService,
		private themeService: ThemeService
	) { }

	ngOnInit() {

		// Subscribe to the dark mode state from the theme service
		// and update the isDarkMode signal accordingly
		this.themeService.darkMode$.subscribe(isDarkMode => {
            this.isDarkMode.set(isDarkMode);
        });

		// Subscribe to the username from the auth state
		// and update the username and shortName properties
		// The shortName is derived from the first two characters of the username
		this.store.select(state => state.auth.username).subscribe(username => {
			this.username = username;
			this.shortName = username.charAt(0).toUpperCase() + username.charAt(1).toLowerCase();
		});

		// Define the menu items for the navigation menu
		// Each item can have a label, icon, and routerLink for navigation
		this.items = [
			{
				separator: true
			},
			{
				label: 'Aktivitäten',
				items: [
					{
						label: 'Dashboard',
						icon: 'pi pi-home',
						routerLink: '/dashboard'
					},
					{
						label: 'Vorgänge',
						icon: 'pi pi-check-square',
						routerLink: '/activities'
					}
				]
			},
			{
				label: 'Gruppen',
				items: [
					{
						label: 'Alle Gruppen',
						icon: 'pi pi-search',
						routerLink: '/groups'
					},
					{
						label: 'Gruppe erstellen',
						icon: 'pi pi-plus',
						routerLink: '/groups/add'
					}
				]
			},
			{
				label: 'Firmen',
				items: [
					{
						label: 'Alle Firmen',
						icon: 'pi pi-search',
						routerLink: '/leads'
					},
					{
						label: 'Firma erstellen',
						icon: 'pi pi-plus',
						routerLink: '/leads/add'
					}
				]
			},
			{
				label: 'Ansprechpartner',
				items: [
					{
						label: 'Alle Ansprechpartner',
						icon: 'pi pi-search',
						routerLink: '/contacts'
					},
					{
						label: 'Ansprechpartner erstellen',
						icon: 'pi pi-plus',
						routerLink: '/contacts/add'
					}
				]
			},
			{
				label: 'Profil',
				items: [
					{
						label: 'Settings',
						icon: 'pi pi-cog'
					},
					{
						label: 'Logout',
						icon: 'pi pi-sign-out',
						command: () => this.logout()
					}
				]
			},
			{
				separator: true
			}
		];
	}

	/**
	 * Logs out the user by dispatching the Logout action.
	 * Displays a success message upon successful logout.
	 * @since 1.0.0
	 * @returns {void}
	 * @todo Localize the logout message for internationalization (i18n).
	 */
	logout(): void {
		this.store.dispatch(new Logout()).subscribe(() => {
			this.messageService.add({ 
				severity: 'success', 
				summary: 'Erfolg', 
				detail: 'Sie wurden erfolgreich abgemeldet.' 
			});
		});
	}

	/**
	 * Toggles the application theme between light and dark mode.
	 * @since 1.0.0
	 * @returns {void}
	 */
	toggleTheme(): void {
		this.themeService.toggleTheme();
	}

}
